// src/components/driver/DriverDashboard.jsx
import React, { useState, useEffect } from 'react';
import { MapPin, Phone, Clock, Users } from 'lucide-react';
import { useStudentContext } from '../../contexts/StudentContext';
import { updateStudentStatus } from '../../services/studentService';
import { updateDriverStatus } from '../../services/driverService';
import { buildRoute, getRouteStats } from '../../services/driverService';
import { checkIn, checkOut } from '../../services/staffService';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';
import {
  getOrCreateSession,
  updateJourneyStatus,
  updateSessionCounts,
  JOURNEY_STAGES,
} from '../../services/driverSessionService';
import { PROGRAM_INFO, STUDENT_STATUS } from '../../utils/constants';
import { getStatusBadgeStyle, getStatusIcon, getDismissalTime } from '../../utils/helpers';
import { styles } from '../../styles/theme';

const DriverDashboard = ({ driver, onLogout }) => {
  const { getStudentsByDriver, getStudentsByDriverForDay } = useStudentContext();
  const [driverStatus, setDriverStatus] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [session, setSession] = useState(null);
  const [journeyStatus, setJourneyStatus] = useState('idle');

  // Time tracking state
  const [staffEntry, setStaffEntry] = useState(null); // active time_entry for today
  const [staffMemberId, setStaffMemberId] = useState(null);
  const [timeLoading, setTimeLoading] = useState(false);
  const [timeError, setTimeError] = useState('');

  // Get students first — needed by useEffects below
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long' });
  const allDriverStudents = getStudentsByDriver(driver.id);
  const todaysStudents = getStudentsByDriverForDay(driver.id, today);
    
  // Load or create today's session on mount
  useEffect(() => {
    const initSession = async () => {
      try {
        const s = await getOrCreateSession(driver);
        setSession(s);
        setJourneyStatus(s.journeyStatus || 'idle');
      } catch (err) {
        console.error('Session init error:', err);
      }
    };
    initSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver.id]);

  useEffect(() => {
    if (!session) return;
    const counts = {
      total: todaysStudents.length,
      pickedUp: todaysStudents.filter(s => s.status === 'picked-up' || s.status === 'delivered').length,
      delivered: todaysStudents.filter(s => s.status === 'delivered').length,
    };
    updateSessionCounts(session.id, counts).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session, todaysStudents.length]);

  // Load staff member record and today's time entry for the driver
  useEffect(() => {
    const loadTimeData = async () => {
      try {
        const today = new Date();
        const m = String(today.getMonth() + 1).padStart(2, '0');
        const d = String(today.getDate()).padStart(2, '0');
        const y = today.getFullYear();
        const todayKey = `${m}-${d}-${y}`;

        // Find staff_member by matching driver name
        const staffSnap = await getDocs(collection(db, 'staff_members'));
        const driverNameNorm = driver.name.toLowerCase().trim();
        const match = staffSnap.docs.find(doc => {
          const s = doc.data();
          const fullName = `${s.firstName} ${s.lastName}`.toLowerCase().trim();
          return fullName === driverNameNorm || fullName.startsWith(driverNameNorm.split(' ')[0].toLowerCase());
        });
        if (!match) return;
        const sid = match.id;
        setStaffMemberId(sid);

        // Load today's active time entry
        const entrySnap = await getDocs(query(
          collection(db, 'time_entries'),
          where('staffId', '==', sid),
          where('date', '==', todayKey),
          where('status', 'in', ['checked-in', 'on-break'])
        ));
        if (!entrySnap.empty) {
          setStaffEntry({ id: entrySnap.docs[0].id, ...entrySnap.docs[0].data() });
        }
      } catch (err) {
        console.error('Time tracking load error:', err);
      }
    };
    loadTimeData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [driver.name]);

  const handleTimeCheckIn = async () => {
    if (!staffMemberId || timeLoading) return;
    setTimeLoading(true);
    setTimeError('');
    try {
      await checkIn(staffMemberId, driver.name, 'center', null);
      // Reload entry
      const today = new Date();
      const m = String(today.getMonth() + 1).padStart(2, '0');
      const d = String(today.getDate()).padStart(2, '0');
      const y = today.getFullYear();
      const todayKey = `${m}-${d}-${y}`;
      const entrySnap = await getDocs(query(
        collection(db, 'time_entries'),
        where('staffId', '==', staffMemberId),
        where('date', '==', todayKey),
        where('status', 'in', ['checked-in', 'on-break'])
      ));
      if (!entrySnap.empty) setStaffEntry({ id: entrySnap.docs[0].id, ...entrySnap.docs[0].data() });
    } catch (err) {
      setTimeError('Check-in failed. Try again.');
    } finally {
      setTimeLoading(false);
    }
  };

  const handleTimeCheckOut = async () => {
    if (!staffEntry || timeLoading) return;
    if (!window.confirm('Check out now?')) return;
    setTimeLoading(true);
    setTimeError('');
    try {
      await checkOut(staffEntry.id, 'center', null);
      setStaffEntry(null);
    } catch (err) {
      setTimeError('Check-out failed. Try again.');
    } finally {
      setTimeLoading(false);
    }
  };

  const getElapsed = () => {
    if (!staffEntry?.checkInTime) return '';
    const mins = (Date.now() - staffEntry.checkInTime.toMillis()) / (1000 * 60);
    return `${Math.floor(mins / 60)}h ${Math.floor(mins % 60)}m`;
  };

  const setJourney = async (newStatus) => {
    if (!session) return;
    try {
      const counts = {
        total: todaysStudents.length,
        pickedUp: todaysStudents.filter(s => s.status === 'picked-up' || s.status === 'delivered').length,
        delivered: todaysStudents.filter(s => s.status === 'delivered').length,
      };
      await updateJourneyStatus(session.id, newStatus, counts);
      setJourneyStatus(newStatus);
    } catch (err) {
      console.error('Journey update error:', err);
    }
  };

const route = buildRoute(todaysStudents);
const stats = getRouteStats(route);

  const handleUpdateStatus = async (studentId, newStatus) => {
    setIsUpdating(true);
    try {
      await updateStudentStatus(studentId, driver.id, newStatus);
      // Auto-advance journey status
      if (newStatus === STUDENT_STATUS.PICKED_UP && journeyStatus !== 'at_school' && journeyStatus !== 'en_route' && journeyStatus !== 'arrived') {
        await setJourney('at_school');
      }
      // Check if all picked up → suggest en route
      const updatedPickedUp = todaysStudents.filter(s => s.id !== studentId && (s.status === 'picked-up' || s.status === 'delivered')).length + 1;
      if (newStatus === STUDENT_STATUS.PICKED_UP && updatedPickedUp >= todaysStudents.length && journeyStatus !== 'arrived') {
        await setJourney('en_route');
      }
      if (newStatus === STUDENT_STATUS.DELIVERED) {
        const updatedDelivered = todaysStudents.filter(s => s.id !== studentId && s.status === 'delivered').length + 1;
        if (updatedDelivered >= todaysStudents.length) {
          await setJourney('arrived');
        }
      }
    } catch (error) {
      alert('Failed to update status. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDriverOnWay = async () => {
    try {
      await updateDriverStatus(driver.id, driver.name, 'on-way');
      setDriverStatus('on-way');
    } catch (error) {
      alert('Failed to update driver status');
    }
  };

  const handleDriverAlert = async () => {
    const reason = prompt('Reason for delay/absence:');
    if (!reason) return;
    
    try {
      await updateDriverStatus(driver.id, driver.name, 'alert', reason);
      alert('Alert sent to staff!');
    } catch (error) {
      alert('Failed to send alert');
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.centerContainer}>
        <button
  onClick={() => {
    const confirmed = window.confirm('Logout and end session?');
    if (confirmed) {
      onLogout();
    }
  }}
  style={{
    position: 'absolute',
    top: '1rem',
    right: '1rem',
    padding: '0.625rem 1.25rem',
    backgroundColor: '#dc2626',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(220, 38, 38, 0.3)',
    transition: 'all 0.2s',
    zIndex: 1000
  }}
  onMouseEnter={(e) => {
    e.target.style.backgroundColor = '#b91c1c';
    e.target.style.transform = 'translateY(-2px)';
  }}
  onMouseLeave={(e) => {
    e.target.style.backgroundColor = '#dc2626';
    e.target.style.transform = 'translateY(0)';
  }}
>
  🚪 Logout
</button>
          <h1 style={styles.headerTitle}>{driver.name} - Driver Dashboard</h1>
          <p style={styles.headerSubtitle}>Today's pickup route and students</p>
        </div>
      </div>

      <div style={styles.centerContainer}>

        {/* ── Time Tracking Strip ── */}
        {staffMemberId && (
          <div style={{ marginBottom: '1rem', padding: '0.85rem 1rem', backgroundColor: staffEntry ? '#f0fdf4' : 'white', borderRadius: '10px', border: `1.5px solid ${staffEntry ? '#10b981' : '#e5e7eb'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <Clock size={16} style={{ color: staffEntry ? '#10b981' : '#9ca3af' }} />
              {staffEntry ? (
                <div>
                  <span style={{ fontWeight: '700', fontSize: '0.875rem', color: '#065f46' }}>
                    ✅ Checked in — {getElapsed()}
                  </span>
                  <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>
                    since {staffEntry.checkInTime?.toDate().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>
              ) : (
                <span style={{ fontSize: '0.875rem', color: '#9ca3af', fontWeight: '500' }}>
                  Not checked in today
                </span>
              )}
            </div>
            <div style={{ display: 'flex', align: 'center', gap: '0.5rem' }}>
              {timeError && <span style={{ fontSize: '0.75rem', color: '#dc2626' }}>{timeError}</span>}
              {!staffEntry ? (
                <button onClick={handleTimeCheckIn} disabled={timeLoading} style={{ padding: '0.45rem 1rem', backgroundColor: timeLoading ? '#9ca3af' : '#10b981', color: 'white', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer' }}>
                  {timeLoading ? '...' : '🕐 Check In'}
                </button>
              ) : (
                <button onClick={handleTimeCheckOut} disabled={timeLoading} style={{ padding: '0.45rem 1rem', backgroundColor: timeLoading ? '#9ca3af' : '#dc2626', color: 'white', border: 'none', borderRadius: '7px', fontWeight: '700', fontSize: '0.82rem', cursor: 'pointer' }}>
                  {timeLoading ? '...' : '🚪 Check Out'}
                </button>
              )}
            </div>
          </div>
        )}
        <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: 'white', borderRadius: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', border: '1px solid #e5e7eb' }}>
          <div style={{ fontSize: '0.8rem', fontWeight: '700', color: '#374151', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Today's Journey
          </div>

          {/* Stage Indicators */}
          <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '1rem' }}>
            {JOURNEY_STAGES.filter(s => s.id !== 'idle').map((stage, idx) => {
              const stages = JOURNEY_STAGES.filter(s => s.id !== 'idle').map(s => s.id);
              const currentIdx = stages.indexOf(journeyStatus);
              const stageIdx = idx;
              const isDone = stageIdx < currentIdx;
              const isCurrent = stageIdx === currentIdx;
              return (
                <div key={stage.id} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
                  <div style={{
                    padding: '0.3rem 0.6rem',
                    borderRadius: '20px',
                    fontSize: '0.72rem',
                    fontWeight: isCurrent ? '700' : '500',
                    backgroundColor: isCurrent ? stage.color : isDone ? '#d1fae5' : '#f3f4f6',
                    color: isCurrent ? 'white' : isDone ? '#065f46' : '#9ca3af',
                    border: `1.5px solid ${isCurrent ? stage.color : isDone ? '#6ee7b7' : '#e5e7eb'}`,
                    whiteSpace: 'nowrap',
                  }}>
                    {stage.icon} {stage.label}
                  </div>
                  {idx < JOURNEY_STAGES.filter(s => s.id !== 'idle').length - 1 && (
                    <div style={{ fontSize: '0.65rem', color: '#d1d5db' }}>→</div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Manual update buttons */}
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {journeyStatus === 'idle' && (
              <button onClick={() => setJourney('left_home')} style={{ padding: '0.5rem 1rem', backgroundColor: '#f59e0b', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}>
                🏠 I've Left Home
              </button>
            )}
            {journeyStatus === 'left_home' && (
              <button onClick={() => setJourney('left_center')} style={{ padding: '0.5rem 1rem', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer' }}>
                🚐 Left Center — Heading to Schools
              </button>
            )}
            {(journeyStatus === 'left_center' || journeyStatus === 'at_school') && (
              <button onClick={() => setJourney('en_route')} style={{ padding: '0.5rem 0.85rem', backgroundColor: '#f97316', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer' }}>
                🚗 All Picked Up — Heading to Stemtree
              </button>
            )}
            {journeyStatus === 'en_route' && (
              <button onClick={() => setJourney('arrived')} style={{ padding: '0.5rem 0.85rem', backgroundColor: '#10b981', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer' }}>
                ✅ Arrived at Stemtree
              </button>
            )}
            {journeyStatus !== 'idle' && journeyStatus !== 'arrived' && (
              <button onClick={() => { if (window.confirm('Report a delay or issue?')) { handleDriverAlert(); } }} style={{ padding: '0.5rem 0.85rem', backgroundColor: '#ef4444', color: 'white', border: 'none', borderRadius: '8px', fontWeight: '600', fontSize: '0.8rem', cursor: 'pointer' }}>
                ⚠️ Report Issue
              </button>
            )}
          </div>
        </div>
        <div style={styles.card}>
          <h2 style={{ marginBottom: '1rem' }}>Today's Route Summary</h2>
          <div style={styles.gridTwoCols}>
            {['pending', 'picked-up', 'delivered'].map(status => {
              const count = stats[status === 'picked-up' ? 'pickedUp' : status];
              const colors = {
                'pending': { bg: '#fef3c7', text: '#92400e' },
                'picked-up': { bg: '#dbeafe', text: '#1e40af' }, 
                'delivered': { bg: '#d1fae5', text: '#065f46' }
              };
              
              return (
                <div key={status} style={{
                  padding: '1rem',
                  backgroundColor: colors[status].bg,
                  borderRadius: '8px',
                  textAlign: 'center'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', color: colors[status].text }}>
                    {count}
                  </div>
                  <div style={{ fontSize: '0.75rem', textTransform: 'capitalize' }}>
                    {status.replace('-', ' ')}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

       {/* Driver Status or Next Pickup */}
<div style={styles.card}>
  {todaysStudents.length === 0 ? (
    // Show Next Pickup Info
    <>
      <h2 style={{ marginBottom: '1rem' }}>📅 Next Pickup Schedule</h2>
      <div style={{
        padding: '2rem',
        backgroundColor: '#f0f9ff',
        borderRadius: '12px',
        border: '2px solid #bfdbfe',
        textAlign: 'center'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🗓️</div>
        <h3 style={{ 
          margin: '0 0 0.5rem 0', 
          color: '#1e40af',
          fontSize: '1.25rem',
          fontWeight: 'bold'
        }}>
          {(() => {
            // Calculate next pickup day
            const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
            const today = new Date().getDay();
            
            // Get all unique pickup days from all students
            const allPickupDays = new Set();
            allDriverStudents.forEach(student => {
              const pickupDays = student.pickupDays || '';
              if (typeof pickupDays === 'string') {
                pickupDays.split(/[\s,]+/).forEach(day => {
                  if (day) allPickupDays.add(day.trim());
                });
              } else if (Array.isArray(pickupDays)) {
                pickupDays.forEach(day => allPickupDays.add(day.trim()));
              }
            });
            
            // Find next pickup day
            let nextDay = null;
            let daysAhead = 1;
            
            while (!nextDay && daysAhead <= 7) {
              const checkDay = daysOfWeek[(today + daysAhead) % 7];
              if (Array.from(allPickupDays).some(day => 
                day.toLowerCase() === checkDay.toLowerCase()
              )) {
                nextDay = checkDay;
              }
              daysAhead++;
            }
            
            if (nextDay) {
              const nextDate = new Date();
              nextDate.setDate(nextDate.getDate() + (daysAhead - 1));
              return `Next pickup: ${nextDay}, ${nextDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
            }
            
            return 'No pickups scheduled this week';
          })()}
        </h3>
        <p style={{ 
          margin: '1rem 0 0 0', 
          color: '#6b7280',
          fontSize: '0.9375rem'
        }}>
          {allDriverStudents.length} student{allDriverStudents.length !== 1 ? 's' : ''} assigned to your route
        </p>
      </div>
    </>
  ) : (
    // Show Driver Status buttons (original code)
    <>
      <h2 style={{ marginBottom: '1rem' }}>Driver Status</h2>
      <div style={{ display: 'flex', gap: '1rem', flexDirection: 'column' }}>
        {!driverStatus ? (
          <button
            style={{
              ...styles.buttonGreen,
              padding: '1.25rem',
              fontSize: '1.125rem',
              fontWeight: 'bold'
            }}
            onClick={handleDriverOnWay}
          >
            🚗 On My Way to Stemtree
          </button>
        ) : (
          <div style={{
            padding: '1.25rem',
            fontSize: '1.125rem',
            fontWeight: 'bold',
            backgroundColor: '#d1fae5',
            color: '#065f46',
            borderRadius: '8px',
            textAlign: 'center',
            border: '2px solid #6ee7b7'
          }}>
            ✅ Driver is on the way to work
          </div>
        )}
        
        <button
          style={{
            ...styles.button,
            padding: '1.25rem',
            fontSize: '1.125rem',
            fontWeight: 'bold',
            backgroundColor: '#ef4444'
          }}
          onClick={handleDriverAlert}
        >
          ⚠️ Report Delay/Emergency
        </button>
      </div>
    </>
  )}
</div>

        {/* Schools and Students */}
        {route.schools.map(school => (
          <div key={school.id} style={styles.card}>
            {/* School Header */}
            <div style={{
              backgroundColor: '#f8fafc',
              padding: '1rem',
              borderRadius: '8px',
              marginBottom: '1rem',
              border: '1px solid #e5e7eb'
            }}>
              <div style={styles.flexBetween}>
                <div>
                  <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.25rem', fontWeight: 'bold' }}>
                    {school.name}
                  </h2>
                  <p style={{ margin: '0', fontSize: '0.875rem', color: '#6b7280', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                    <MapPin style={{ width: '16px', height: '16px' }} />
                    {school.address}
                  </p>
                </div>
                <button 
                  style={styles.button}
                  onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(school.address)}`, '_blank')}
                >
                  Navigate
                </button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Phone style={{ width: '16px', height: '16px' }} />
                  {school.phone}
                </span>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                  <Clock style={{ width: '16px', height: '16px' }} />
                  Dismissal: {getDismissalTime(school)}
                </span>
              </div>
            </div>

            {/* Students List */}
            <h3 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <Users style={{ width: '18px', height: '18px' }} />
              Students ({school.students.length})
            </h3>
            
            {school.students.map(student => (
              <div key={student.id} style={{
                border: '2px solid #e5e7eb',
                borderRadius: '12px',
                padding: '1rem',
                marginBottom: '1rem'
              }}>
                <div style={styles.flexBetween}>
                  <div>
                    <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1.125rem', fontWeight: 'bold' }}>
                      {student.firstName} {student.lastName}
                    </h4>
                    <p style={{ margin: '0', fontSize: '0.875rem', color: '#6b7280' }}>Grade {student.grade}</p>
                    {student.medicalConditions !== 'None' && (
                      <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#dc2626', fontWeight: '500' }}>
                        ⚠️ {student.medicalConditions}
                      </p>
                    )}
                  </div>
                  <span style={getStatusBadgeStyle(student.status, styles.badge)}>
                    {getStatusIcon(student.status)} {student.status.replace('-', ' ').toUpperCase()}
                  </span>
                </div>
                
                <div style={{
                  backgroundColor: '#f9fafb',
                  borderRadius: '8px',
                  padding: '0.75rem',
                  margin: '0.75rem 0',
                  fontSize: '0.875rem'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <Phone style={{ width: '16px', height: '16px', color: '#3b82f6' }} />
                    <span style={{ fontWeight: '500' }}>{student.parentName}</span>
                    <span style={{ color: '#6b7280' }}>- {student.parentPhone}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <MapPin style={{ width: '16px', height: '16px', color: '#10b981', marginTop: '0.125rem' }} />
                    <span>{student.dropoffAddress}</span>
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                    <strong>Authorized pickup:</strong> {Array.isArray(student.authorizedPickup) ? student.authorizedPickup.join(', ') : student.authorizedPickup}
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {student.status === STUDENT_STATUS.PENDING && (
                    <button
                      style={styles.button}
                      onClick={() => handleUpdateStatus(student.id, STUDENT_STATUS.PICKED_UP)}
                      disabled={isUpdating}
                    >
                      ✓ Mark Picked Up
                    </button>
                  )}
                  {student.status === STUDENT_STATUS.PICKED_UP && (
                    <>
                      <button
                        style={styles.buttonGreen}
                        onClick={() => window.open(`https://maps.google.com/?q=${encodeURIComponent(PROGRAM_INFO.address)}`, '_blank')}
                      >
                        Navigate to Stemtree
                      </button>
                      <button
                        style={styles.buttonOrange}
                        onClick={() => handleUpdateStatus(student.id, STUDENT_STATUS.DELIVERED)}
                        disabled={isUpdating}
                      >
                        ✓ Mark Delivered
                      </button>
                    </>
                  )}
                  {student.status === STUDENT_STATUS.DELIVERED && (
                    <div style={{
                      flex: 1,
                      backgroundColor: '#d1fae5',
                      color: '#065f46',
                      padding: '0.75rem',
                      borderRadius: '8px',
                      textAlign: 'center',
                      fontWeight: '600',
                      border: '2px solid #6ee7b7'
                    }}>
                      ✅ Delivered at {student.deliveryTime}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DriverDashboard;